import { isRep, offerStore } from '@store/orders';
import { convertUtcToLocalAndFormat } from '@utils/dateFormat';
import { formatAmount } from '@utils/utils';
import { createSignal, For, Show } from 'solid-js';
import { MoneyInput } from '@components/TextField/MoneyInput';
import { Stack } from '@atgfr8/atg-ui-tailwind';
import { SquareIconButton } from '@components/Grid/components/SquareIconButton';
import {
  CheckIcon,
  CurrencyUsdIcon,
  CloseCircleOutlineIcon,
} from '@atgfr8/icons';
import { HoverPop } from '@components/HoverPop/HoverPop';
import { userStore } from '@store/user';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { FeatureFlags } from '@constants/featureFlags';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';

import {
  rejectOffer,
  approveOffer,
  carrierStatusStyles,
  expireBid,
  statusStyles,
  counterOffer,
  isLoadAvailable,
} from './helpers';

export const OfferHistory = (props: {
  changeTrackingSource?: ChangeTrackingSource;
}) => {
  const headerStyles =
    'font-semibold text-black text-sm py-2 sticky top-0 z-10 bg-white';
  const cellStyles = 'flex text-sm py-1 items-center';
  const isOfferManager = isFeatureFlagEnabled(FeatureFlags.OfferManagerUI);
  const canEdit = () => isRep() || isOfferManager();
  return (
    <div class="border border-armstrong-blue rounded-md">
      <div class="text-lg text-black font-semibold px-2 py-1 bg-[#e4edf4] rounded-t-md border-b border-armstrong-blue">
        Offer History
      </div>
      <div class="grid grid-cols-[1fr_1fr_1fr_1fr_1fr_1fr_1fr_0.3fr] bg-white rounded-b-md shadow-md  text-black max-h-[420px] overflow-y-auto px-2">
        <div class={`${headerStyles} px-1 col-span-2`}>Carrier</div>
        <div class={headerStyles}>Requested By</div>
        <div class={headerStyles}>Bid Info</div>
        <div class={headerStyles}>Counter</div>
        <div class={headerStyles}>Action By</div>
        <div class={headerStyles}>Actions</div>
        <div class={headerStyles}>Live</div>
        <div class="col-span-8 h-[2px] bg-gray-300 my-[1px] mx-1"></div>
        <For
          each={offerStore.bids.filter(
            (bid) => bid.userId !== userStore.user.id,
          )}
        >
          {(offer) => {
            const [counterAmount, setCounterAmount] = createSignal(
              offer.counterAmount,
            );
            const [isLoading, setIsLoading] = createSignal(false);
            const disabled = () => {
              return (
                offer.status === 'Expired' ||
                offer.status === 'Booked' ||
                offer.status === 'Assigned' ||
                isLoading() ||
                !isLoadAvailable() ||
                !canEdit()
              );
            };
            return (
              <div
                class={`col-span-8 grid grid-cols-subgrid border-b border-gray-300 items-center ${offer.status === 'Expired' ? ' opacity-40 text-gray-500 bg-gray-300 ' : ''}`}
              >
                <div
                  class={`${cellStyles} flex-col gap-1 col-span-2 items-start min-w-0`}
                >
                  <a
                    href={`/carrier/details/${offer.carrierId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-armstrong-blue text-sm underline"
                  >
                    {offer.carrierName}
                  </a>
                  <div class="flex flex-wrap items-center gap-1 w-full min-w-0">
                    <div
                      class={`text-sm ${carrierStatusStyles[offer.carrierStatus]} rounded-xl px-2 py-px w-fit y-fit text-center`}
                    >
                      {offer.carrierStatus}
                    </div>
                    <div
                      class={
                        'text-sm rounded-xl px-2 py-px w-fit y-fit text-center bg-gray-200'
                      }
                    >
                      MC#{offer.mc}
                    </div>
                    <Show when={offer.bidType === 'External'}>
                      <div
                        class={
                          'text-sm rounded-xl px-2 py-px w-fit y-fit text-center text-armstrong-primary bg-[#daf2ff]'
                        }
                      >
                        External
                      </div>
                    </Show>
                  </div>
                </div>
                <div class={`${cellStyles} flex flex-col gap-1 items-start`}>
                  <div>{offer.requestedBy}</div>
                  <div class={cellStyles}>
                    {convertUtcToLocalAndFormat(
                      offer.createdDate,
                      'MM/dd/yyyy h:mm a',
                    )}
                  </div>
                </div>
                <div class={`${cellStyles} flex-col items-start`}>
                  <div class={`${cellStyles} font-roboto font-semibold`}>
                    {formatAmount(offer.amount, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                  </div>
                  <div
                    class={`rounded-md px-2 py-1 text-sm y-fit w-fit font-roboto  ${statusStyles[offer.status]}`}
                  >
                    {offer.status}
                  </div>
                </div>
                <div class={`${cellStyles} w-[90%]`}>
                  <MoneyInput
                    disabled={disabled()}
                    value={counterAmount()}
                    onInput={(e: { target: { value: string } }) => {
                      setCounterAmount(Number(e.target.value));
                    }}
                    onKeyDown={async (e: { key: string }) => {
                      if (e.key === 'Enter') {
                        const amount = counterAmount();
                        if (amount === null || amount === 0) {
                          return;
                        }
                        setIsLoading(true);
                        await counterOffer(
                          offer.id,
                          amount,
                          props.changeTrackingSource,
                        );
                        setIsLoading(false);
                      }
                    }}
                  />
                </div>
                <div class={`${cellStyles} flex-col gap-1 items-start`}>
                  <div>{offer.counteredBy ?? '-'}</div>
                  <div>
                    {offer.reviewedDate
                      ? convertUtcToLocalAndFormat(
                          offer.reviewedDate,
                          'MM/dd/yyyy h:mm a',
                        )
                      : ''}
                  </div>
                </div>

                <div class={`${cellStyles} text-gray-500 `}>
                  <Stack direction="row" gap="5px">
                    <HoverPop
                      render={!disabled()}
                      anchor={
                        <SquareIconButton
                          disabled={disabled()}
                          onClick={async () => {
                            setIsLoading(true);
                            await approveOffer(
                              offer.id,
                              props.changeTrackingSource,
                            );
                            setIsLoading(false);
                          }}
                          icon={<CheckIcon />}
                          hoverColors={['#1c6051']}
                          backgroundColor="#248670"
                        />
                      }
                    >
                      <div class="p-2 rounded-md text-white text-sm bg-gray-700">
                        Approve bid
                      </div>
                    </HoverPop>
                    <HoverPop
                      render={!disabled()}
                      anchor={
                        <SquareIconButton
                          onClick={async () => {
                            setIsLoading(true);
                            await rejectOffer(
                              offer.id,
                              props.changeTrackingSource,
                            );
                            setIsLoading(false);
                          }}
                          disabled={disabled()}
                          icon={<CloseCircleOutlineIcon />}
                          hoverColors={['#b00020']}
                          backgroundColor="#b00020"
                        />
                      }
                    >
                      <div class="p-2 rounded-md text-white text-sm bg-gray-700">
                        Decline bid
                      </div>
                    </HoverPop>
                    <HoverPop
                      render={!disabled()}
                      anchor={
                        <SquareIconButton
                          disabled={disabled()}
                          onClick={async () => {
                            if (
                              counterAmount() === null ||
                              counterAmount() === 0
                            ) {
                              return;
                            }
                            setIsLoading(true);
                            await counterOffer(
                              offer.id,
                              counterAmount()!,
                              props.changeTrackingSource,
                            );
                            setIsLoading(false);
                          }}
                          icon={<CurrencyUsdIcon />}
                          hoverColors={['#f7b151']}
                          backgroundColor="#f7b151"
                        />
                      }
                    >
                      <div class="p-2 rounded-md text-white text-sm bg-gray-700">
                        Counter bid
                      </div>
                    </HoverPop>
                  </Stack>
                </div>
                <div class={`${cellStyles} ml-2`}>
                  <input
                    type="checkbox"
                    class="accent-armstrong-blue hover:cursor-pointer disabled:pointer-events-none"
                    disabled={disabled()}
                    checked={offer.status !== 'Expired'}
                    onChange={() => {
                      void expireBid(offer.id, props.changeTrackingSource);
                    }}
                  />
                </div>
              </div>
            );
          }}
        </For>
      </div>
    </div>
  );
};
